import { OtlFormSubmission } from "./OtlFormSubmission";

export const DRAFT_KEY = "irc:otl:draft:v1";
export const DISABLED_KEY = "irc:otl:draft-disabled";
export const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
export type Attempt = { submissionKey: string; snapshot: OtlFormSubmission };
export type Draft = {
  version: 1;
  period: string;
  updatedAt: number;
  values: Partial<OtlFormSubmission>;
  attempt?: Attempt;
};
const fields = [
  "firstName",
  "lastName",
  "community",
  "fileNumber",
  "dateOfBirth",
  "email",
  "phoneNumber",
  "physicalAddress",
  "mailingAddress",
  "houseHoldMembers",
  "gasStorePreference",
  "groceryStorePreference",
  "emergencyContact",
  "releaseAccept",
];
export function draftValues(
  values: Partial<OtlFormSubmission>,
): Partial<OtlFormSubmission> {
  return JSON.parse(
    JSON.stringify(
      Object.fromEntries(
        Object.entries(values).filter(([key]) => fields.includes(key)),
      ),
    ),
  );
}
const addressFields = [
  "line1",
  "line2",
  "city",
  "outsideIsrCity",
  "provinceOrTerritory",
  "country",
  "postalCode",
];
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function validAddress(value: unknown): boolean {
  return (
    isRecord(value) &&
    Object.entries(value).every(
      ([key, entry]) =>
        addressFields.includes(key) &&
        (typeof entry === "string" ||
          (key === "provinceOrTerritory" && typeof entry === "number")),
    )
  );
}
function validValues(value: unknown, member = false): boolean {
  if (!isRecord(value)) return false;
  if (
    member &&
    (typeof value.memberId !== "string" ||
      !value.memberId ||
      typeof value.sameAddressAsApplicant !== "boolean")
  )
    return false;
  const allowed = member
    ? [
        "memberId",
        "firstName",
        "lastName",
        "sameAddressAsApplicant",
        "physicalAddress",
        "mailingAddress",
        "fileNumber",
        "dateOfBirth",
      ]
    : fields;
  return Object.entries(value).every(([key, entry]) => {
    if (!allowed.includes(key)) return false;
    if (key === "physicalAddress" || key === "mailingAddress")
      return validAddress(entry);
    if (key === "emergencyContact")
      return (
        isRecord(entry) &&
        Object.entries(entry).every(
          ([name, item]) =>
            ["firstName", "lastName", "phoneNumber"].includes(name) &&
            typeof item === "string",
        )
      );
    if (key === "houseHoldMembers")
      return (
        Array.isArray(entry) && entry.every((item) => validValues(item, true))
      );
    if (key === "releaseAccept" || key === "sameAddressAsApplicant")
      return typeof entry === "boolean";
    if (key === "dateOfBirth" && entry === null) return true;
    if (key === "community")
      return typeof entry === "string" || typeof entry === "number";
    return typeof entry === "string";
  });
}
export function readDraft(
  storage: Storage,
  period: string,
  now = Date.now(),
): Draft | undefined {
  const raw = storage.getItem(DRAFT_KEY);
  if (!raw) return;
  try {
    const draft = JSON.parse(raw);
    if (
      draft.version !== 1 ||
      draft.period !== period ||
      !Number.isFinite(draft.updatedAt) ||
      draft.updatedAt > now ||
      now - draft.updatedAt > RETENTION_MS ||
      !validValues(draft.values) ||
      (draft.attempt &&
        (typeof draft.attempt.submissionKey !== "string" ||
          !validValues(draft.attempt.snapshot)))
    ) {
      storage.removeItem(DRAFT_KEY);
      return;
    }
    return { ...draft, values: draftValues(draft.values) };
  } catch {
    storage.removeItem(DRAFT_KEY);
    return;
  }
}
export function writeDraft(
  storage: Storage,
  period: string,
  values: Partial<OtlFormSubmission>,
  attempt?: Attempt,
) {
  storage.setItem(
    DRAFT_KEY,
    JSON.stringify({
      version: 1,
      period,
      updatedAt: Date.now(),
      values: draftValues(values),
      attempt,
    }),
  );
}
